import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Printer, Lock, LockOpen } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MOIS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const JOURS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

export default function AdminCalendar() {
  const [planning, setPlanning] = useState({ heures: [], reservations: [], fermetures: [] });
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/admin/rdv/planning`, { withCredentials: true })
      .then((r) => setPlanning(r.data))
      .catch(() => toast.error("Erreur de chargement du planning"));
  }, []);
  useEffect(load, [load]);

  const items = [
    ...planning.reservations.filter((x) => x.rdv_date),
    ...(planning.autres || []).map((a) => ({ ...a, prenom: a.nom, nom: `— ${a.sujet}`, poste_principal: a.contact, isLibre: true })),
  ];
  const byDate = {};
  items.forEach((r) => { (byDate[r.rdv_date] = byDate[r.rdv_date] || []).push(r); });

  const isFerme = (date, heure) =>
    planning.fermetures.some((f) => f.date === date && (f.heure === heure || f.heure === null));
  const jourFerme = (date) => planning.fermetures.some((f) => f.date === date && f.heure === null);

  const toggleSlot = async (date, heure) => {
    setBusy(true);
    try {
      const action = isFerme(date, heure) ? "ouvrir" : "fermer";
      await axios.post(`${API}/admin/rdv/${action}`, { date, heure }, { withCredentials: true });
      toast.success(action === "fermer" ? "Créneau fermé" : "Créneau ouvert");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const toggleJour = async (date) => {
    setBusy(true);
    try {
      const action = jourFerme(date) ? "ouvrir" : "fermer";
      await axios.post(`${API}/admin/rdv/${action}`, { date, heure: null }, { withCredentials: true });
      toast.success(action === "fermer" ? "Journée fermée" : "Journée ouverte");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const offset = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [...Array(offset).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  const iso = (d) => `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const nav = (dir) => setCursor(new Date(year, month + dir, 1));
  const todayIso = new Date().toISOString().slice(0, 10);
  const dayRdv = (selected ? byDate[selected] || [] : []).sort((a, b) => (a.rdv_heure || "").localeCompare(b.rdv_heure || ""));
  const takenBy = {};
  dayRdv.forEach((r) => { if (r.rdv_heure) takenBy[r.rdv_heure] = r; });

  const printDay = () => {
    if (!selected || dayRdv.length === 0) return;
    const dateFr = selected.split("-").reverse().join("/");
    const rows = dayRdv.map((r) =>
      `<tr><td>${r.rdv_heure}</td><td>${r.prenom} ${r.nom}</td><td>${r.reference}</td><td>${r.poste_principal}</td><td>${r.telephone}</td><td></td></tr>`
    ).join("");
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>Rendez-vous ${dateFr} — D2R</title><style>
      body{font-family:Arial,sans-serif;padding:40px;color:#111}
      h1{font-size:20px;text-transform:uppercase;letter-spacing:2px}
      .sub{color:#C5A059;font-size:12px;text-transform:uppercase;letter-spacing:3px}
      table{width:100%;border-collapse:collapse;margin-top:24px}
      td,th{border:1px solid #ccc;padding:10px;text-align:left;font-size:13px}
      th{background:#f5f5f5;text-transform:uppercase;font-size:11px;letter-spacing:1px}
    </style></head><body>
      <p class="sub">Dream2Reality Football Agency</p>
      <h1>Rendez-vous bureau — ${dateFr}</h1>
      <p>${dayRdv.length} rendez-vous · Imprimé le ${new Date().toLocaleDateString("fr-FR")}</p>
      <table><tr><th>Heure</th><th>Joueur</th><th>Référence</th><th>Poste</th><th>Téléphone</th><th>Présent</th></tr>${rows}</table>
    </body></html>`);
    w.document.close();
    w.print();
  };

  return (
    <div data-testid="admin-calendar" className="grid lg:grid-cols-12 gap-8">
      <div className="lg:col-span-7 border border-[#27272A] bg-[#121214] p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-xl uppercase font-medium">{MOIS[month]} <span className="text-[#C5A059]">{year}</span></h3>
          <div className="flex gap-2">
            <button onClick={() => nav(-1)} data-testid="calendar-prev" className="p-2 border border-[#27272A] text-[#71717A] hover:text-[#C5A059] hover:border-[#C5A059] transition-colors" aria-label="Mois précédent"><ChevronLeft size={16} /></button>
            <button onClick={() => nav(1)} data-testid="calendar-next" className="p-2 border border-[#27272A] text-[#71717A] hover:text-[#C5A059] hover:border-[#C5A059] transition-colors" aria-label="Mois suivant"><ChevronRight size={16} /></button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-1">
          {JOURS.map((j) => <p key={j} className="text-center text-[10px] uppercase tracking-widest text-[#71717A] py-2">{j}</p>)}
          {cells.map((d, i) => {
            if (!d) return <div key={`empty-${i}`} />;
            const dayIso = iso(d);
            const count = (byDate[dayIso] || []).length;
            const ferme = jourFerme(dayIso);
            return (
              <button
                key={dayIso}
                onClick={() => setSelected(dayIso)}
                data-testid={`calendar-day-${dayIso}`}
                className={`aspect-square flex flex-col items-center justify-center text-sm border transition-colors ${
                  selected === dayIso ? "border-[#C5A059] bg-[#C5A059]/10 text-white"
                  : ferme ? "border-red-900/50 text-red-400/70"
                  : dayIso === todayIso ? "border-[#C5A059]/40 text-white"
                  : "border-transparent text-[#A1A1AA] hover:border-[#27272A]"
                }`}
              >
                {d}
                {ferme && <Lock size={9} className="mt-0.5 text-red-400/70" />}
                {!ferme && count > 0 && <span className="w-4 h-4 mt-1 text-[9px] leading-4 text-center bg-[#C5A059] text-black font-bold rounded-full">{count}</span>}
              </button>
            );
          })}
        </div>
      </div>

      <div className="lg:col-span-5 border border-[#27272A] bg-[#121214] p-6">
        <div className="flex items-center justify-between gap-4 mb-6">
          <h3 className="font-display text-xl uppercase font-medium">
            {selected ? `Rendez-vous du ${selected.split("-").reverse().join("/")}` : "Sélectionnez un jour"}
          </h3>
          {selected && dayRdv.length > 0 && (
            <button onClick={printDay} data-testid="rdv-print-button" className="flex items-center gap-2 border border-[#C5A059] text-[#C5A059] px-4 py-2 text-[10px] uppercase tracking-[0.2em] hover:bg-[#C5A059] hover:text-black transition-colors">
              <Printer size={13} /> Imprimer
            </button>
          )}
        </div>

        {selected && (
          <div className="mb-6" data-testid="slot-manager">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#71717A]">Créneaux du jour</p>
              <button onClick={() => toggleJour(selected)} disabled={busy} data-testid="slot-toggle-day"
                className={`flex items-center gap-2 text-[10px] uppercase tracking-widest px-3 py-1.5 border transition-colors ${jourFerme(selected) ? "border-emerald-500/60 text-emerald-400" : "border-red-500/60 text-red-400"}`}>
                {jourFerme(selected) ? <><LockOpen size={12} /> Ouvrir la journée</> : <><Lock size={12} /> Fermer la journée</>}
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {planning.heures.map((h) => {
                const pris = takenBy[h];
                const ferme = isFerme(selected, h);
                return (
                  <button
                    key={h}
                    onClick={() => !pris && !jourFerme(selected) && toggleSlot(selected, h)}
                    disabled={busy || !!pris || jourFerme(selected)}
                    data-testid={`slot-${h}`}
                    title={pris ? `Pris par ${pris.prenom} ${pris.nom}` : ferme ? "Fermé — cliquer pour rouvrir" : "Libre — cliquer pour fermer"}
                    className={`py-2.5 text-xs font-medium border transition-colors ${
                      pris ? "border-[#C5A059]/50 bg-[#C5A059]/10 text-[#C5A059] cursor-default"
                      : ferme ? "border-red-900/50 text-red-400/70 hover:border-red-400"
                      : "border-[#27272A] text-[#A1A1AA] hover:border-red-500/60 hover:text-red-400"
                    } ${jourFerme(selected) && !pris ? "opacity-40" : ""}`}
                  >
                    {h}
                    <span className="block text-[8px] uppercase tracking-widest mt-0.5">
                      {pris ? `${pris.prenom} ${pris.nom.charAt(0)}.` : ferme ? "Fermé" : "Libre"}
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-[#3f3f46] mt-3 leading-relaxed">
              Cliquez sur un créneau libre pour le fermer, sur un créneau fermé pour le rouvrir. Les créneaux pris par un joueur se ferment automatiquement.
            </p>
          </div>
        )}

        {selected && dayRdv.length === 0 && <p className="text-[#71717A] text-sm">Aucun rendez-vous ce jour.</p>}
        {!selected && <p className="text-[#71717A] text-sm">{items.length} rendez-vous au total. Cliquez sur un jour pour voir et gérer ses créneaux.</p>}
        <div className="space-y-3">
          {dayRdv.map((r) => (
            <div key={r.id} data-testid={`rdv-item-${r.id.slice(0, 8)}`} className="border border-[#27272A] p-4 flex items-center justify-between gap-4">
              <div>
                <p className="font-medium">{r.prenom} {r.nom} <span className="text-[#C5A059] text-xs">{r.reference}</span></p>
                <p className="text-xs text-[#71717A] mt-1">{r.poste_principal} · {r.telephone}</p>
              </div>
              <span className="font-display text-lg text-[#C5A059] font-medium whitespace-nowrap">{r.rdv_heure}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
